/**
 * `ConnectionStatus` — a thin presentational banner that surfaces the
 * SignalR connection lifecycle to the user.
 *
 * Responsibilities (requirements coverage R4.6, R9.3, R9.5):
 *   - Visualize the realtime connection state so the user can tell whether
 *     their chat session is live, recovering, or hard-broken (R4.6).
 *   - Provide a clear failure surface with an explicit retry affordance
 *     when the auto-reconnect policy has been exhausted (R9.3).
 *   - Stay readable in every state: the slate-900 backdrop is paired with
 *     foreground tokens that all exceed WCAG AA 4.5:1 contrast (R9.5).
 *
 * Behavior contract (design.md §Frontend Layer › ConnectionStatus):
 *   - `idle`         → render nothing. Before any connect attempt there is
 *                       no useful signal to show.
 *   - `connected`    → render nothing. A live, healthy connection should
 *                       not steal screen real estate from the chat itself.
 *   - `connecting`   → informational banner ("接続中…"). Announced
 *                       politely so it does not interrupt the user.
 *   - `reconnecting` → warning banner ("再接続中…"). Still polite — the
 *                       client is actively recovering on its own.
 *   - `failed`       → error banner ("接続が切れました"). Announced
 *                       assertively so screen readers surface the loss of
 *                       realtime immediately. When `onRetry` is provided,
 *                       the banner exposes a retry button; otherwise the
 *                       button is omitted (some callers prefer to handle
 *                       retries elsewhere, e.g. by reloading the page).
 *
 * Dependency direction (design.md §File Structure Plan):
 *   This component depends ONLY on the `ConnectionState` type re-exported
 *   from `../api/signalRClient`. It does not call the SignalR client, hooks,
 *   or any other module; the consumer (typically `ChatView`) is responsible
 *   for sourcing the current state via `useSignalRConnection` and wiring up
 *   `onRetry` to `client.connect(negotiate)`.
 */

import type { JSX } from 'react';

import type { ConnectionState } from '../api/signalRClient';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ConnectionStatusProps {
  /** Current SignalR lifecycle state. Drives both visibility and tone. */
  readonly state: ConnectionState;
  /**
   * Optional retry callback. When present and the connection has `failed`,
   * the banner renders a button that invokes this handler on click. Callers
   * that don't want a retry affordance simply omit this prop.
   */
  readonly onRetry?: () => void;
}

// ---------------------------------------------------------------------------
// State → presentation mapping
// ---------------------------------------------------------------------------

/**
 * Internal descriptor for a renderable banner state. Centralizing the
 * mapping keeps the JSX below short and makes it impossible to forget a
 * presentation attribute (live politeness, tone) for a new state.
 *
 * `liveness` controls `aria-live`:
 *   - `'polite'` for transient, recoverable states (connecting/reconnecting).
 *   - `'assertive'` for hard failures the user needs to notice now.
 *
 * `tone` is the CSS modifier appended to `.connection-status` so the
 * stylesheet can colour the banner per state without us emitting inline
 * styles.
 */
interface BannerDescriptor {
  readonly tone: 'info' | 'warning' | 'error';
  readonly liveness: 'polite' | 'assertive';
  readonly message: string;
}

/**
 * Resolve the banner descriptor for a given connection state, or `null`
 * when the state should not render anything. Encapsulating the switch
 * here makes the render path a single, declarative expression and gives
 * the type system a chance to enforce exhaustiveness on the input.
 */
function describe(state: ConnectionState): BannerDescriptor | null {
  switch (state) {
    case 'idle':
    case 'connected':
      return null;
    case 'connecting':
      return {
        tone: 'info',
        liveness: 'polite',
        message: '接続中…',
      };
    case 'reconnecting':
      return {
        tone: 'warning',
        liveness: 'polite',
        message: '再接続中…',
      };
    case 'failed':
      return {
        tone: 'error',
        liveness: 'assertive',
        message: '接続が切れました',
      };
    default: {
      // Exhaustiveness guard. If a new state is added to `ConnectionState`
      // and the switch above doesn't handle it, this assignment fails at
      // compile time. The runtime fallback (no banner) is conservative —
      // an unknown state isn't worth interrupting the user over.
      const _exhaustive: never = state;
      void _exhaustive;
      return null;
    }
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConnectionStatus(props: ConnectionStatusProps): JSX.Element | null {
  const { state, onRetry } = props;

  const descriptor = describe(state);
  if (descriptor === null) {
    return null;
  }

  const showRetry = state === 'failed' && typeof onRetry === 'function';

  return (
    <div
      className={`connection-status connection-status--${descriptor.tone}`}
      role="status"
      aria-live={descriptor.liveness}
    >
      <span className="connection-status__message">{descriptor.message}</span>
      {showRetry ? (
        <button
          type="button"
          className="connection-status__retry"
          onClick={onRetry}
          aria-label="接続を再試行"
        >
          再試行
        </button>
      ) : null}
    </div>
  );
}
